import React, { useMemo, useState } from "react";
import {
    Field,
    TagPicker,
    TagPickerList,
    TagPickerInput,
    TagPickerControl,
    TagPickerProps,
    TagPickerOption,
    TagPickerGroup,
    Spinner,
    makeStyles,
    mergeClasses,
    tokens,
    InteractionTagPrimary,
    InteractionTag,
    InteractionTagSecondary,
} from "@fluentui/react-components";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";

const useStyles = makeStyles({
    field: {},
    group: {
        maxHeight: "4.75rem",
        overflowY: "auto",
        width: "100%",
    },
    option: {
        paddingLeft: "20px",
    },
    optionText: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    menuLabel: {
        textAlign: "center",
        color: tokens.colorNeutralForeground4,
    },
    list: {
        maxHeight: "32vh",
    },
    tag: {
        height: "28px",
        "& button": {
            justifyContent: "start",
        },
        "& span": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            padding: "0",
        },
    },
    controlBase: {
        "&&&": {
            "border-bottom-color": tokens.colorNeutralStrokeAccessible,
        },
        // Fix: mobile webviews don't render the blinking caret inside inputs
        // within a fixed-position overlay. A compositing layer forces proper rendering.
        "& input": {
            transform: "translateZ(0)",
            "caret-color": "auto",
        },
    },
    controlHover: {
        "&&&:hover": {
            "border-color": tokens.colorNeutralStrokeAccessible,
        },
    },
    inputCollapsed: {
        flex: "0 0 0px",
        minWidth: "0px",
        padding: "0",
    },
});

export interface MultiSelectTagPickerProps extends LocalizeContextProps {
    label: string;
    testid: string;
    options: string[];
    selectedOptions: string[];
    onSelectionChange: (selectedOptions: string[]) => void;
    onInput?: (query: string) => void;
    allowCustomInput?: boolean;
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    itemLimit?: number;
    isLoading?: boolean;
    validationMessage?: string;
    noOptionText?: string | JSX.Element;
    displayFullLineTag?: boolean;
    showDownArrow?: boolean;
    disableAutoFocusOverride?: boolean;
    renderTag?: (x: any) => JSX.Element;
    renderOption?: (x: any) => JSX.Element;
}

export const MultiSelectTagPickerBase = (props: MultiSelectTagPickerProps): JSX.Element => {
    const styles = useStyles();
    const [query, setQuery] = useState("");
    const previousSelectedOptionsRef = React.useRef(props.selectedOptions);

    const isDarkMode = document.querySelector("[data-theme]")?.getAttribute("data-theme") !== "default";

    // disableAutoFocus is supported by the underlying combobox but not exposed in TagPickerProps.
    // By default, keep previous behavior: disable while query is empty and enable while typing.
    // Some callers can opt in to a fixed value via disableAutoFocusOverride.
    const disableAutoFocus = {
        disableAutoFocus: props.disableAutoFocusOverride ?? (query ? false : true),
    } as Partial<TagPickerProps>;

    const filteredOptions = useMemo(() => {
        const available = props.options.filter((opt) => !props.selectedOptions.includes(opt));
        if (!query) return available;
        return available.filter((opt) => opt.toLocaleLowerCase().includes(query.toLocaleLowerCase()));
    }, [props.options, props.selectedOptions, query]);

    const clearQuery = () => {
        setQuery("");
        if (props.onInput) {
            props.onInput("");
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) {
            return;
        }

        clearQuery();
    };

    React.useEffect(() => {
        const selectedOptionsChanged =
            previousSelectedOptionsRef.current.length !== props.selectedOptions.length ||
            previousSelectedOptionsRef.current.some((option, index) => option !== props.selectedOptions[index]);

        if (query && selectedOptionsChanged) {
            clearQuery();
        }

        previousSelectedOptionsRef.current = props.selectedOptions;
    }, [props.selectedOptions, query]);

    const onOptionSelect: TagPickerProps["onOptionSelect"] = (_e, data) => {
        if (props.selectedOptions.includes(data.value)) {
            // The toggled item was already selected — this is an explicit removal via the
            // tag dismiss button. Filter it out of the controlled list.
            props.onSelectionChange(props.selectedOptions.filter((opt) => opt !== data.value));
        } else {
            // The toggled item is new — append it. data.selectedOptions cannot be used
            // directly because Fluent UI's internal state only knows about options visible in
            // the filtered dropdown and has no knowledge of options already selected (which
            // are excluded from the list). Using data.value avoids the length-comparison
            // trap where the TagPicker's partial view makes an addition look like a removal.
            props.onSelectionChange([...props.selectedOptions, data.value]);
        }
        clearQuery();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && query.trim()) {
            const trimmedQuery = query.trim();

            // Check if the query matches exactly one of the available options (case-insensitive)
            const exactMatch = props.options.find(
                (opt) => !props.selectedOptions.includes(opt) && opt.toLowerCase() === trimmedQuery.toLowerCase()
            );

            if (exactMatch) {
                // Select the matching option
                props.onSelectionChange([...props.selectedOptions, exactMatch]);
                clearQuery();
                e.preventDefault();
                return;
            }

            // Value does not match any option exactly
            // Check if there are filtered options available in the dropdown
            const hasFilteredOptions = filteredOptions.length > 0;

            if (hasFilteredOptions) {
                // If there are filtered options available, don't prevent default.
                // Let the native TagPicker handle selecting the highlighted/focused option.
                // This applies whether allowCustomInput is true or false.
                clearQuery();
                return;
            }

            // No filtered options found
            if (props.allowCustomInput) {
                // Add as custom value and preserve it
                if (!props.selectedOptions.includes(trimmedQuery)) {
                    props.onSelectionChange([...props.selectedOptions, trimmedQuery]);
                }
                clearQuery();
                e.preventDefault();
            }
        }
    };
    const hideDownArrow = props.showDownArrow === false;
    const limitReached = props.itemLimit != null && props.selectedOptions.length >= props.itemLimit;
    return (
        <div onBlur={handleBlur}>
            <Field
                className={styles.field}
                label={props.label}
                required={props.required}
                validationMessage={props.validationMessage}
            >
                <TagPicker
                    onOptionSelect={onOptionSelect}
                    selectedOptions={props.selectedOptions}
                    disabled={!!props.disabled}
                    appearance={isDarkMode ? "filled-darker" : "outline"}
                    {...disableAutoFocus}
                >
                    <TagPickerControl
                        expandIcon={limitReached || hideDownArrow ? null : undefined}
                        style={{ "--fui-TagPickerControl-aside-width": hideDownArrow ? "5px" : "18px" }}
                        className={mergeClasses(styles.controlBase, !props.disabled && styles.controlHover)}
                    >
                        <TagPickerGroup aria-label={`Selected ${props.label}`} className={styles.group}>
                            {props.selectedOptions.map((opt) => (
                                <InteractionTag
                                    style={{ width: props.displayFullLineTag ? "100%" : "" }}
                                    data-testid={opt}
                                    key={opt}
                                    shape="rounded"
                                    value={opt}
                                    className={styles.tag}
                                    title={opt}
                                    size="small"
                                    appearance={isDarkMode ? "outline" : "filled"}
                                >
                                    <InteractionTagPrimary
                                        style={{
                                            flexGrow: props.displayFullLineTag ? "1" : "",
                                        }}
                                        hasSecondaryAction
                                    >
                                        {props.renderTag ? props.renderTag(opt) : opt}
                                    </InteractionTagPrimary>
                                    <InteractionTagSecondary aria-label="Remove" />
                                </InteractionTag>
                            ))}
                        </TagPickerGroup>
                        {!limitReached && (
                            <TagPickerInput
                                aria-label={props.label}
                                placeholder={props.placeholder}
                                value={query}
                                translate="no"
                                onChange={(e) => {
                                    setQuery(e.target.value);
                                    if (props.onInput) {
                                        props.onInput(e.target.value);
                                    }
                                }}
                                onKeyDown={handleKeyDown}
                                data-testid={props.testid}
                                title=""
                                autoComplete="off"
                            />
                        )}
                    </TagPickerControl>
                    {
                        <TagPickerList className={styles.list}>
                            {props.isLoading ? (
                                <div>
                                    <Spinner size="small" />
                                </div>
                            ) : filteredOptions.length > 0 ? (
                                filteredOptions.map((opt) => (
                                    <TagPickerOption text={opt} className={styles.option} value={opt} key={opt}>
                                        {props.renderOption ? (
                                            props.renderOption(opt)
                                        ) : (
                                            <span className={styles.optionText}>{opt}</span>
                                        )}
                                    </TagPickerOption>
                                ))
                            ) : (
                                <div style={{ padding: tokens.spacingHorizontalM }}>
                                    {props.noOptionText ||
                                        (props.translate
                                            ? (props.translate("FLUENT9.NO_OPTIONS") as string)
                                            : "FLUENT9.NO_OPTIONS")}
                                </div>
                            )}
                        </TagPickerList>
                    }
                </TagPicker>
            </Field>
        </div>
    );
};

export const MultiSelectTagPicker = withLocalize(MultiSelectTagPickerBase);
